import React from 'react'
import PropTypes from 'prop-types'
import styled, { css, keyframes } from 'styled-components'
import { connect } from 'react-redux'
import Icon from '../../general/Icon'
import { desktop, VIEWPORT_DESKTOP } from '../../../styles/media'
import Tooltip from '../../general/Tooltip'

const HoverAction = ({ active, color, icon, isEditingCollectionMobile, loading, minWidth, onClick, opensInfoModal, readOnly, showIconWithValue, title, value, viewport }) => {
	const empty = !active && !value

	return value || viewport === VIEWPORT_DESKTOP || isEditingCollectionMobile ? (
		<Tooltip title={title}>
			<StyledHoverAction
				active={active}
				color={color}
				readOnly={readOnly}
				minWidth={minWidth}
				onClick={(event) => {
					event.preventDefault()
					event.stopPropagation()

					if (onClick !== null) {
						onClick(event)
					}
				}}
				icon={icon}
				loading={loading ? 1 : undefined}
				empty={empty}
				visible={!!(value || isEditingCollectionMobile)}
				opensInfoModal={opensInfoModal}
				value={value}
				showIconWithValue={showIconWithValue}
			>
				<Icon name={icon} />
				{value && !(icon === 'heart' && value === 1) && <HoverActionValue>{value}{icon !== 'heart' && <>x</>}</HoverActionValue>}
				<HoverText>+1</HoverText>
				{opensInfoModal && <ModalIcon><Icon name="information-circle" /></ModalIcon>}
			</StyledHoverAction>
		</Tooltip>
	) : null
}

HoverAction.propTypes = {
	active: PropTypes.bool,
	color: PropTypes.string,
	loading: PropTypes.bool,
	icon: PropTypes.string.isRequired,
	isEditingCollectionMobile: PropTypes.bool,
	minWidth: PropTypes.bool,
	onClick: PropTypes.func,
	opensInfoModal: PropTypes.bool,
	readOnly: PropTypes.bool,
	showIconWithValue: PropTypes.bool,
	title: PropTypes.string,
	value: PropTypes.any,
	viewport: PropTypes.string.isRequired
}

HoverAction.defaultProps = {
	active: false,
	color: null,
	isEditingCollectionMobile: false,
	loading: false,
	minWidth: false,
	onClick: null,
	opensInfoModal: false,
	readOnly: false,
	showIconWithValue: false,
	title: null,
	value: null
}

const pulse = keyframes`
	0% {
		box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.2);
	}

	100% {
		box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
	}
`

const pulseAnimation = css`
	animation: ${pulse} 2s infinite;
`

export const HoverActionValue = styled.div`
	font-size: 0.9rem;
	font-weight: 800;
`

const HoverText = styled.div`
	display: none;
	font-size: 0.8rem;
	font-weight: 600;
	margin-left: 0.15rem;
`

export const StyledHoverAction = styled.button`
	background: ${props => props.theme.id === 'dark' ? 'rgba(255, 255, 255, 0.05)' : props.theme.backgroundSemi};
	border: 0;
	border-radius: 4px;
	color: ${props => props.theme.textSlightlyLight};
	height: 32px;
	opacity: ${props => props.visible ? 1 : 0};
	transition: background 0.1s, color 0.1s;
	width: 32px;

	align-items: center;
	display: flex;
	justify-content: center;

	svg {
		height: 16px;
		width: 16px;
		transition: color 0.2s, transform 0.1s;

		${props => props.value && !(props.icon === 'heart' && props.value === 1) && `
			margin: 0 0.25rem 0 0;
		`}

		${props => props.icon === 'add' && `
			height: 24px;
			width: 24px;
		`}

		${props => !(props.empty || props.showIconWithValue || (props.icon && !props.value) || (props.value && props.icon === 'heart' && props.value === 1)) && `
			display: none;
		`}
	}

	${props => props.minWidth && `
		min-width: 50px;

		height: 28px;
		padding: 0 0.25rem;

		svg {
			height: 18px;
			width: 18px;
		}

		${props.theme.id === 'light' && `
			background: rgba(0, 0, 0, 0.05);
		`}
	`}

	${props => props.visible && `
		svg {
			height: 16px;
			width: 16px;

			${props.value && !(props.icon === 'heart' && props.value === 1) && `
				margin: 0 0.15rem 0 0;
			`}
		}
	`}

	${props => props.active && `
		background: ${props.color ? props.theme[props.color] : props.theme.primary};
		color: #FFF;
		cursor: pointer;
	`}

	${props => props.visible && !props.empty && `
		background: ${props.color ? props.theme[`${props.color}Light`] : props.theme.primaryLight};
		color: ${props.theme.text};
		cursor: pointer;

		${props.theme.id === 'dark' ? `
			background: ${props.color ? props.theme[`${props.color}Light`] : props.theme.primaryLight};
		` : ''}

		svg {
			color: ${props.color ? props.theme[props.color] : props.theme.primary};

			${props.theme.id === 'dark' && props.color === 'red' && `
				color: ${props.theme.white};
			`}
		}

		${props.value < 3 && !props.color ? `
			background: ${props.theme.id === 'dark' ? 'transparent' : props.theme.primaryVeryLightHover};
			box-shadow: inset 0 0 0 2px ${props.theme.primaryLight};
		` : ''}

		${props.value < 3 && props.color && props.color !== 'sage' && props.theme.id !== 'dark' ? `
			box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.1);
		` : ''}

		${props.color === 'sage' && props.theme.id === 'dark' ? `
			color: rgba(255, 255, 255, 0.5);
		` : ''}
	`}

	${props => props.loading && pulseAnimation}

	${props => props.readOnly && `
		pointer-events: none;
	`}

	&:hover {
	}

	${desktop`
		${props => props.visible && props.empty && `
			background: rgba(255, 255, 255, 0.4);
			color: rgba(0, 0, 0, 0.3) !important;
		`}

		${props => !props.active && !props.readOnly && !props.opensInfoModal && `
			&:hover {
				background: ${props.theme.id === 'dark' ? 'rgba(255, 255, 255, 0.1)' : props.theme.background};
				box-shadow: ${props.theme.shadowSearch};
				color: ${props.color && props.color !== 'sage' ? props.theme[props.color] : props.theme.primary};
				cursor: pointer;

				${props.color === 'secondary' && props.theme.id === 'dark' && `
					color: ${props.theme.white};
				`}

				svg {
					display: block;
					transform: scale(1.1);
				}
			}
		`}

		${props => props.visible && !props.empty && `
			&:hover {
				background: ${props.theme.id === 'dark' ? 'rgba(255, 255, 255, 0.1)' : props.theme.background};
				color: ${props.color && props.color !== 'sage' ? props.theme[props.color] : props.theme.primary};

				${props.color === 'sage' ? `
					svg {
						color: ${props.theme.primary};
						display: block;
					}
				` : ''}
			}
		`}

		${props => props.visible && `
			&:hover {
				${HoverText} {
					display: block;
				}

				${HoverActionValue} {
					display: none;
				}
			}
		`}

		${props => props.opensInfoModal && `
			&:hover {
				${ModalIcon} {
					opacity: 1;
				}
			}
		`}
	`}
`

const ModalIcon = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	z-index: ${props => props.theme.zLayer1};

	height: 100%;
	width: 100%;

	align-items: center;
	display: flex;
	justify-content: center;

	background: ${props => props.theme.background};
	border-radius: 4px;
	color: ${props => props.theme.textLight};
	opacity: 0;
	transition: 0.1s;
`

const enhance = connect(state => ({
	viewport: state.app.viewport
}))

export default enhance(HoverAction)
